import type {BarSeriesOption} from 'echarts';
import {EChartsOption} from 'echarts';
import {BarChart} from 'echarts/charts';
import {GridComponent, GridComponentOption, LegendComponent, TitleComponent, TooltipComponent, TooltipComponentOption} from 'echarts/components';
import * as echarts from 'echarts/core';
import {CanvasRenderer} from 'echarts/renderers';
import Vue from 'vue';
import {Component, Prop, Ref, Watch} from 'vue-property-decorator';

echarts.use([TitleComponent, TooltipComponent, GridComponent, LegendComponent, BarChart, CanvasRenderer]);

interface ChartSeriesData extends BarSeriesOption {}

interface ChartData {
    xAxisData: string[];
    seriesData: ChartSeriesData[];
    title?: string;
    yAxisLabel?: string;
}

type ECOption = EChartsOption & {
    tooltip?: TooltipComponentOption;
    grid?: GridComponentOption;
};

@Component
export default class AreasAndEquipmentsCountChart extends Vue {
    @Prop({required: true, type: Object as () => ChartData})
    readonly chartData!: ChartData;

    @Ref('chartContainer')
    readonly chartContainer!: HTMLDivElement;

    private chartInstance: echarts.ECharts | null = null;

    public chartHeight: string = '300px';
    public chartWidth: string = '100%';

    mounted() {
        this.initChart();
        window.addEventListener('resize', this.handleResize);
    }

    beforeDestroy() {
        if (this.chartInstance) {
            this.chartInstance.dispose();
            this.chartInstance = null;
        }
        window.removeEventListener('resize', this.handleResize);
    }

    @Watch('chartData', {deep: true})
    onChartDataChanged(newData: ChartData) {
        if (this.chartInstance && newData) {
            this.updateChartOptions();
        } else if (!newData && this.chartInstance) {
            this.chartInstance.clear();
        } else if (newData && !this.chartInstance) {
            this.initChart();
        }
    }

    private initChart(): void {
        if (this.chartContainer) {
            this.chartInstance = echarts.init(this.chartContainer);
            this.updateChartOptions();
        } else {
            console.error('Chart container element not found.');
        }
    }

    private updateChartOptions(): void {
        if (!this.chartInstance || !this.chartData) {
            return;
        }

        const option: ECOption = {
            title: {
                text: this.chartData.title || '',
                left: 'center',
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow',
                },
            },
            legend: {
                top: '3%',
                left: 'center',
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '10%',
                containLabel: true,
            },
            xAxis: {
                type: 'category',
                data: this.chartData.xAxisData || [],
                axisLabel: {
                    interval: 0,
                    rotate: 0,
                },
            },
            yAxis: {
                type: 'value',
                name: this.chartData.yAxisLabel || '',
            },
            series: (this.chartData.seriesData as BarSeriesOption[]) || [],
        };

        this.chartInstance.setOption(option, true);
    }

    private handleResize(): void {
        if (this.chartInstance) {
            this.chartInstance.resize();
        }
    }

    get containerStyle() {
        return {
            width: this.chartWidth,
            height: this.chartHeight,
        };
    }
}
